import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Tab,
  Tabs,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  EmptyState,
  GlassButton,
  ImageDropUploader,
  LoadingOverlay,
  PageHeader,
  PagePaper,
  folderTabs,
} from 'dashboard/components/common';
import LinkListEditor from './LinkListEditor';
import HeaderStyleControls from './HeaderStyleControls';
import FooterStyleControls from './FooterStyleControls';
import FooterColumns from './FooterColumns';
import HeaderExtras from './HeaderExtras';
import GlobalPreview from './GlobalPreview';
import SiteTab from './SiteTab';

/**
 * Global design — the header and footer every page inherits.
 *
 * This exists because a header copied into each page is a header that drifts.
 * Add a menu item and you have added it to the page you were editing and
 * nowhere else, which is how a five-page site ends up with five different
 * menus. Setting it once is not a convenience, it is the only version that
 * stays correct.
 *
 * Switching a section off keeps its content. That is the same rule the
 * content library runs on, and it matters here for a specific reason: a brand
 * trying a headerless landing page should be able to change their mind
 * without retyping the menu.
 */

/**
 * One left edge for everything in a tab.
 *
 * MUI insets a FormControlLabel by -11px, so that a switch's glyph — not its
 * ripple area — lines up with text above it. That is right for a row sitting
 * directly in the column, and wrong for one nested inside a Box, which starts
 * from its own edge instead. This panel had both, so two switches a line
 * apart began at different places, and neither began where the headings and
 * the button groups did.
 *
 * Rather than tune each one, every row in a tab starts at the tab's own edge.
 * The switch glyph then sits a few pixels in from the headings, which is the
 * trade: one ragged edge of three kinds of control, against one straight edge
 * with the switches very slightly inset.
 */
const PANEL = { '& .MuiFormControlLabel-root': { ml: 0, mr: 0 } };

const COLOR_FIELDS = [
  { key: 'backgroundColor', label: 'Background' },
  { key: 'textColor', label: 'Text' },
];

function ColorRow({ value, onChange, backgroundLabel, hint }) {
  return (
    <Box>
    <Stack direction='row' spacing='12px'>
      {COLOR_FIELDS.map((f) => (
        <TextField
          key={f.key}
          size='small'
          type='color'
          label={f.key === 'backgroundColor' && backgroundLabel ? backgroundLabel : f.label}
          value={value[f.key] || '#ffffff'}
          onChange={(e) => onChange({ [f.key]: e.target.value })}
          sx={{ width: 110, '& input': { height: 34, padding: '2px', cursor: 'pointer' } }}
          InputLabelProps={{ shrink: true }}
        />
      ))}
    </Stack>
    {hint && (
      <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
        {hint}
      </Typography>
    )}
    </Box>
  );
}

/**
 * The optional dark-mode pair for the header or the footer.
 *
 * Off by default and off is the right answer for most brands: blank means the
 * colours are worked out from the light ones, the same way the sections are,
 * so a brand that never opens this still gets a bar that reads at night.
 *
 * It is a switch and not just two more colour boxes because a colour input
 * cannot say "nothing". `type="color"` always holds a colour — there is no
 * empty state to leave it in — so without the switch there would be no way
 * back to automatic once you had touched it.
 */
function DarkColorRow({ value, onChange, what }) {
  const dark = value?.dark || {};
  const on = Boolean(dark.backgroundColor || dark.textColor);

  return (
    <Box mt='14px'>
      <FormControlLabel
        control={(
          <Switch
            checked={on}
            onChange={(e) => onChange({
              dark: e.target.checked
                ? { backgroundColor: '#12151b', textColor: '#e5e7eb' }
                : { backgroundColor: '', textColor: '' },
            })}
          />
        )}
        label='Set the dark-mode colours myself'
      />
      {on ? (
        <Stack direction='row' spacing='12px' mt='6px'>
          <TextField
            size='small'
            type='color'
            label='Background'
            value={dark.backgroundColor || '#12151b'}
            onChange={(e) => onChange({ dark: { ...dark, backgroundColor: e.target.value } })}
            sx={{ width: 110, '& input': { height: 34, padding: '2px', cursor: 'pointer' } }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size='small'
            type='color'
            label='Text'
            value={dark.textColor || '#e5e7eb'}
            onChange={(e) => onChange({ dark: { ...dark, textColor: e.target.value } })}
            sx={{ width: 110, '& input': { height: 34, padding: '2px', cursor: 'pointer' } }}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      ) : (
        <Typography variant='caption' color='text.secondary' display='block'>
          Worked out from the colours above: a light {what} is darkened and the
          text flipped so it still reads. Switch this on if you would rather say.
        </Typography>
      )}
    </Box>
  );
}

export default function GlobalDesign() {
  const [selectedBrand] = useOutletContext();
  const [value, setValue] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [brandLogo, setBrandLogo] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [tab, setTab] = useState('header');
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  // The autosave timer fires after the render that scheduled it, so it reads
  // the value through a ref rather than closing over a stale one.
  const valueRef = useRef(null);

  const load = useCallback(() => {
    if (!selectedBrand) return;
    setLoading(true);
    API.getSiteGlobal({ brand_id: selectedBrand })
      .then((res) => { setValue(res); setDirty(false); })
      .catch((err) => toast.error(err?.message || 'Could not load global design'))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => { load(); }, [load]);

  // Only for the preview's fallback title, so an untouched header still shows
  // something recognisable rather than the word "Brand".
  useEffect(() => {
    if (!selectedBrand) return;
    API.getBrandById?.({ brand_id: selectedBrand })
      .then((b) => {
        setBrandName(b?.name || '');
        // The profile endpoint and the brand list disagree about where the
        // logo sits, so check both rather than silently deciding the brand
        // has none and disabling the option that uses it.
        setBrandLogo(b?.logo_upload_path || b?.brand_id?.logo_upload_path || b?.logo || '');
      })
      .catch(() => { /* preview falls back to a generic label */ });
  }, [selectedBrand]);

  useEffect(() => { valueRef.current = value; }, [value]);

  // One reading of the logo choice, used by the buttons, the warning and the
  // size slider alike.
  const logoSource = value?.header?.logo?.source || 'brand';

  const patch = (part, changes) => {
    setValue((v) => ({ ...v, [part]: { ...v[part], ...changes } }));
    setDirty(true);
  };
  const toggle = (key, on) => {
    setValue((v) => ({ ...v, [key]: on }));
    setDirty(true);
  };

  /**
   * Publish what is on screen, not what was last written.
   *
   * Publishing used to be blocked while there were unsaved changes, because
   * the autosave would always have caught up within the second. With the
   * draft saved on request instead, that guard would just be a disabled
   * button with no way to understand it — so publishing saves first.
   * Publishing a site and finding the morning's changes missing would be the
   * worst version of this.
   */
  const publish = () => {
    setPublishing(true);
    (dirty ? save({ silent: true }) : Promise.resolve())
      .then(() => API.publishSiteGlobal({ brand_id: selectedBrand }))
      .then((res) => {
        setValue((v) => ({ ...v, ...res }));
        toast.success('Your header and footer are live');
      })
      .catch((err) => toast.error(err?.message || 'Publish failed'))
      .finally(() => setPublishing(false));
  };

  /**
   * Saving the draft.
   *
   * `silent` suppresses the toast for a save that is part of something else —
   * publishing saves first, and two notifications for one button press reads
   * as a stutter.
   */
  const save = useCallback((opts = {}) => {
    const current = valueRef.current;
    if (!current || !selectedBrand) return Promise.resolve();
    setSaving(true);
    return API.updateSiteGlobal({
      brand_id: selectedBrand,
      patch: {
        headerEnabled: current.headerEnabled,
        header: current.header,
        footerEnabled: current.footerEnabled,
        footer: current.footer,
        notFound: current.notFound,
      },
    })
      .then((res) => {
        setValue(res);
        setDirty(false);
        setSavedAt(Date.now());
        if (!opts.silent) toast.success('Saved — every page uses this');
      })
      .catch((err) => toast.error(err?.message || 'Save failed'))
      .finally(() => setSaving(false));
  }, [selectedBrand]);

  /**
   * Changes are a draft until they are saved.
   *
   * This screen used to write 900ms after any change, and the reason given
   * was that the page editor next door saved as you type — so a Save button
   * here would have been the odd one out. That is no longer true: the page
   * editor holds its work and saves when asked, and so does this. Changing a
   * colour to see how it looks should not change the stored site.
   *
   * The draft/publish split stays either way. It is what stops a half-built
   * menu reaching visitors.
   */
  const discard = () => {
    if (!window.confirm('Throw away the changes you have made since the last save?')) return;
    load();
  };

  // Closing the tab with unsaved work gets the browser's own warning — the
  // only thing that can stop it taking the work with it.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Global design'
          subtitle='The header and footer every page on your site uses. Changes save as a draft on their own; publish puts them in front of visitors.'
          actions={
            <>
              {/* Two steps on purpose. Saving keeps a draft; publishing is
                  what visitors see. Without the split, every keystroke on a
                  half-built menu would be live on the brand's own domain. */}
              {value && (
                <Chip
                  size='small'
                  color={value.hasUnpublishedChanges ? 'warning' : 'success'}
                  variant={value.hasUnpublishedChanges ? 'filled' : 'outlined'}
                  label={value.hasUnpublishedChanges ? 'Not published yet' : 'Live'}
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                />
              )}
              {/* Says what happened to the last change, because autosave with
                  no acknowledgement is indistinguishable from no save. */}
              {value && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  noWrap
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                  {saving ? 'Saving…' : dirty ? 'Unsaved changes' : savedAt ? 'Draft saved' : ''}
                </Typography>
              )}
              {/* Shown only when there is something to save or throw away.
                  Buttons that do nothing most of the time teach people to
                  ignore them. */}
              {dirty && (
                <>
                  <GlassButton onClick={discard} disabled={saving}>
                    Cancel
                  </GlassButton>
                  <GlassButton
                    variant='contained'
                    onClick={() => save()}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save draft'}
                  </GlassButton>
                </>
              )}
              <GlassButton
                variant='contained'
                onClick={publish}
                disabled={publishing || saving || !value}
                disabledReason=''
              >
                {publishing ? 'Publishing…' : 'Publish'}
              </GlassButton>
            </>
          }
        />

        {!selectedBrand && <EmptyState>Select a brand to set its global design.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && !value} minHeight={260}>
            {value && (
              <Grid container spacing='24px'>
                <Grid item xs={12} md={7}>
                  {/* One section at a time.
                      
                      Header, footer and the not-found page were one column you scrolled
                      through, and each has grown a spacing group, a type group and a finish
                      group since. Three settings screens stacked end to end read as one
                      very long mess, and the preview — the thing you are watching — sat
                      beside whichever part you happened to have scrolled to. */}
                  {/* Not `variant="scrollable"`: three tabs this short fit in
                      this column, and a scroller reserves 40px for an arrow at
                      each end whether or not it is usable. That reservation was
                      enough to push the row over the edge, at which point every
                      change of tab scrolled the selected one back into view and
                      the whole row slid sideways under the pointer. */}
                  <Tabs
                    value={tab}
                    onChange={(_e, v) => setTab(v)}
                    sx={{ ...folderTabs, mb: '18px' }}
                  >
                    <Tab value='header' label='Header' />
                    <Tab value='footer' label='Footer' />
                    <Tab value='notFound' label='Page not found' />
                    <Tab value='site' label='Site' />
                  </Tabs>

                  {/* Not part of the global design document, so it neither
                      reads `value` nor goes through Publish — these save
                      themselves the moment they are switched. Kept on this
                      screen anyway because this is where you come to change
                      what every page has in common. */}
                  {tab === 'site' && <SiteTab brandId={selectedBrand} />}

                  {tab === 'header' && (<Box sx={PANEL}>
                  {/* ── Header ───────────────────────────────────────── */}
                  <Stack direction='row' alignItems='center' justifyContent='space-between' mb='10px'>
                    <Typography variant='h6' fontWeight='700'>Header</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value.headerEnabled}
                          onChange={(e) => toggle('headerEnabled', e.target.checked)}
                        />
                      }
                      label={value.headerEnabled ? 'On' : 'Off'}
                    />
                  </Stack>
                  <Typography variant='body2' color='text.secondary' mb='14px'>
                    Switching it off keeps everything below — turn it back on and your menu is
                    still here.
                  </Typography>

                  <Stack spacing='14px' sx={{ opacity: value.headerEnabled ? 1 : 0.5 }}>
                    <TextField
                      size='small'
                      label='Site name'
                      value={value.header.title}
                      onChange={(e) => patch('header', { title: e.target.value })}
                      placeholder={brandName || 'Your brand'}
                      helperText='Leave blank to use your brand name, so a rename updates it everywhere.'
                      fullWidth
                    />
                    {/* Not a URL field.
                        Asking someone to paste the address of a file they
                        already uploaded in brand settings is asking them to go
                        and find it. The uploaded logo is the default; the
                        other two options exist for real reasons — a wordmark
                        is often wrong in a 40px bar, and some headers are just
                        the site name set in type. */}
                    <Box>
                      <Typography variant='body2' fontWeight='600' mb='8px'>Logo</Typography>
                      {/* The stored value can be absent — the logo settings
                          were added after these documents were first written
                          — and "absent" means the brand logo, which is what
                          the buttons below show. Everything that reads the
                          choice has to read it the same way, or the screen
                          disagrees with itself: the warning about a missing
                          brand logo used to check the raw value, so it stayed
                          hidden until you clicked another option and back,
                          which wrote the value the display had been implying
                          all along. */}
                      <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={logoSource}
                        onChange={(_e, v) => v && patch('header', {
                          logo: { ...(value.header.logo || {}), source: v },
                        })}
                        sx={{ mb: '10px', flexWrap: 'wrap' }}
                      >
                        {/* Not disabled when the brand has no logo: a greyed
                            button with no explanation reads as broken. It is
                            selectable, and the note below says what to do. */}
                        <ToggleButton value='brand'>My brand logo</ToggleButton>
                        <ToggleButton value='custom'>Different image</ToggleButton>
                        <ToggleButton value='none'>Name only</ToggleButton>
                      </ToggleButtonGroup>

                      {!brandLogo && logoSource === 'brand' && (
                        <Typography variant='caption' color='warning.main' display='block' mb='8px'>
                          No brand logo uploaded yet, so the header shows your site name for now.
                          Upload one in Brand settings and it appears here automatically.
                        </Typography>
                      )}

                      {logoSource === 'custom' && (
                        <Box sx={{ mb: '10px' }}>
                          {/* Upload, not a URL. Everywhere else in the
                              dashboard an image is uploaded; asking for a web
                              address here meant hosting the file somewhere
                              else first, which is not a thing most brand
                              owners have a way to do. */}
                          <ImageDropUploader
                            brandId={selectedBrand}
                            currentSrc={value.header.logo?.customUrl || ''}
                            onUploaded={(url) => patch('header', {
                              logo: { ...(value.header.logo || {}), customUrl: url },
                            })}
                          />
                          <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
                            A compact version works best in a header.
                          </Typography>
                        </Box>
                      )}

                      {logoSource !== 'none' && (
                        <Box sx={{ px: '4px' }}>
                          <Typography variant='caption' color='text.secondary'>
                            Height in the bar — {value.header.logo?.height || 30}px
                          </Typography>
                          <Slider
                            size='small'
                            min={16}
                            max={80}
                            value={value.header.logo?.height || 30}
                            onChange={(_e, v) => patch('header', {
                              logo: { ...(value.header.logo || {}), height: v },
                            })}
                          />
                        </Box>
                      )}
                    </Box>
                    <ColorRow
                      value={value.header}
                      onChange={(c) => patch('header', c)}
                      // With frosted glass on, the bar is only partly this
                      // colour — calling it "Background" while the site shows
                      // something translucent is what made the choice look
                      // like it did nothing.
                      backgroundLabel={value.header.style?.glass?.enabled ? 'Glass tint' : 'Background'}
                      hint={value.header.style?.glass?.enabled
                        ? `Tinting the blur at ${value.header.style?.glass?.amount ?? 60}% — lower that and this colour shows less.`
                        : ''}
                    />
                    <DarkColorRow
                      value={value.header}
                      onChange={(c) => patch('header', c)}
                      what='bar'
                    />
                    <Box>
                      <Typography variant='body2' fontWeight='600' mb='8px'>Arrangement</Typography>
                      <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={value.header.layout || 'left'}
                        onChange={(_e, v) => v && patch('header', { layout: v })}
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <ToggleButton value='left'>Logo left</ToggleButton>
                        <ToggleButton value='center'>Logo centred</ToggleButton>
                        <ToggleButton value='split'>Menu either side</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={value.header.sticky}
                          onChange={(e) => patch('header', { sticky: e.target.checked })}
                        />
                      }
                      label='Stay visible when scrolling'
                    />

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={value.header.themeToggle?.enabled !== false}
                            onChange={(e) => patch('header', {
                              themeToggle: { ...(value.header.themeToggle || {}), enabled: e.target.checked },
                            })}
                          />
                        }
                        label='Let visitors switch to dark mode'
                      />
                      {value.header.themeToggle?.enabled !== false && (
                        <FormControl size='small' sx={{ minWidth: 200, mt: '8px', display: 'block' }}>
                          <InputLabel>Starts as</InputLabel>
                          <Select
                            label='Starts as'
                            value={value.header.themeToggle?.defaultMode || 'system'}
                            onChange={(e) => patch('header', {
                              themeToggle: { ...(value.header.themeToggle || {}), defaultMode: e.target.value },
                            })}
                            fullWidth
                          >
                            <MenuItem value='system'>Whatever their device uses</MenuItem>
                            <MenuItem value='light'>Light</MenuItem>
                            <MenuItem value='dark'>Dark</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                    <Box>
                      <Typography variant='body2' fontWeight='600' mb='8px'>Also in the bar</Typography>
                      <HeaderExtras
                        extras={value.header.extras || []}
                        onChange={(extras) => patch('header', { extras })}
                      />
                    </Box>

                    <Box>
                      <Typography variant='body2' fontWeight='600' mb='2px'>Menu links</Typography>
                      <Typography variant='caption' color='text.secondary' display='block' mb='8px'>
                        Use the ↳ button to put links underneath one — that link
                        becomes a dropdown, and those can nest again.
                      </Typography>
                      <LinkListEditor
                        links={value.header.links}
                        onChange={(links) => patch('header', { links })}
                        addLabel='Add menu link'
                        nestable
                      />
                    </Box>

                    <Box>
                      <Typography variant='body2' fontWeight='600' mb='8px'>Style</Typography>
                      <HeaderStyleControls
                        style={value.header.style}
                        onChange={(style) => patch('header', { style })}
                        sticky={Boolean(value.header.sticky)}
                        onStickyChange={(sticky) => patch('header', { sticky })}
                      />
                    </Box>
                  </Stack>

                  </Box>)}

                  {tab === 'footer' && (<Box sx={PANEL}>
                  {/* ── Footer ───────────────────────────────────────── */}
                  <Stack direction='row' alignItems='center' justifyContent='space-between' mb='10px'>
                    <Typography variant='h6' fontWeight='700'>Footer</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value.footerEnabled}
                          onChange={(e) => toggle('footerEnabled', e.target.checked)}
                        />
                      }
                      label={value.footerEnabled ? 'On' : 'Off'}
                    />
                  </Stack>

                  <Stack spacing='14px' sx={{ opacity: value.footerEnabled ? 1 : 0.5 }}>
                    <TextField
                      size='small'
                      label='Footer text'
                      value={value.footer.text}
                      onChange={(e) => patch('footer', { text: e.target.value })}
                      placeholder={`© ${new Date().getFullYear()} ${brandName || 'Your brand'}`}
                      helperText='Leave blank for a copyright line with your brand name.'
                      fullWidth
                    />
                    <ColorRow value={value.footer} onChange={(c) => patch('footer', c)} />
                    <DarkColorRow
                      value={value.footer}
                      onChange={(c) => patch('footer', c)}
                      what='footer'
                    />

                    <Box>
                      <Typography variant='body2' fontWeight='600' mb='8px'>Style</Typography>
                      <FooterStyleControls
                        style={value.footer.style}
                        onChange={(style) => patch('footer', { style })}
                      />
                    </Box>
                    <Box>
                      <Typography variant='body2' fontWeight='600' mb='8px'>Columns</Typography>
                      <FooterColumns
                        columns={value.footer.columns || []}
                        onChange={(columns) => patch('footer', { columns })}
                        brandId={selectedBrand}
                      />
                    </Box>

                    {(value.footer.columns || []).length === 0 && (
                      <Box>
                        <Typography variant='body2' fontWeight='600' mb='8px'>Footer links</Typography>
                        {/* Hidden once columns exist: the same links in two
                            places would render twice. */}
                        <LinkListEditor
                          links={value.footer.links}
                          onChange={(links) => patch('footer', { links })}
                          addLabel='Add footer link'
                        />
                      </Box>
                    )}
                  </Stack>
                  </Box>)}

                  {tab === 'notFound' && (<Box sx={PANEL}>
                  {/* ── Page not found ───────────────────────────────────── */}
                  <Stack direction='row' alignItems='center' justifyContent='space-between' mb='10px'>
                    <Typography variant='h6' fontWeight='700'>“Page not found”</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(value.notFound?.enabled)}
                          onChange={(e) => patch('notFound', { enabled: e.target.checked })}
                        />
                      }
                      label={value.notFound?.enabled ? 'Custom' : 'Default'}
                    />
                  </Stack>
                  <Typography variant='body2' color='text.secondary' mb='14px'>
                    What someone sees at an address that does not exist on your site. Leave it on
                    Default and we use sensible wording.
                  </Typography>

                  <Stack spacing='14px' sx={{ opacity: value.notFound?.enabled ? 1 : 0.5 }}>
                    <TextField
                      size='small'
                      label='Heading'
                      value={value.notFound?.title || ''}
                      onChange={(e) => patch('notFound', { title: e.target.value })}
                      placeholder="We couldn't find that page."
                      fullWidth
                    />
                    <TextField
                      size='small'
                      label='Message'
                      value={value.notFound?.message || ''}
                      onChange={(e) => patch('notFound', { message: e.target.value })}
                      placeholder='It may have moved, or the address may be slightly off.'
                      multiline
                      minRows={2}
                      fullWidth
                    />

                    <Box>
                      <Typography variant='caption' color='text.secondary' display='block' mb='6px'>
                        A picture for this screen — optional
                      </Typography>
                      <ImageDropUploader
                        brandId={selectedBrand}
                        currentSrc={value.notFound?.image || ''}
                        onUploaded={(url) => patch('notFound', { image: url })}
                      />
                      {value.notFound?.image && (
                        <GlassButton
                          size='small'
                          sx={{ mt: '6px' }}
                          onClick={() => patch('notFound', { image: '' })}
                        >
                          Remove the picture
                        </GlassButton>
                      )}
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing='10px'>
                      <TextField
                        size='small'
                        label='Button text'
                        value={value.notFound?.buttonLabel || ''}
                        onChange={(e) => patch('notFound', { buttonLabel: e.target.value })}
                        placeholder='Back to the homepage'
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size='small'
                        label='Button goes to'
                        value={value.notFound?.buttonUrl || ''}
                        onChange={(e) => patch('notFound', { buttonUrl: e.target.value })}
                        placeholder='/'
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  </Stack>
                  </Box>)}
                </Grid>

                {/* Sticky so the preview stays put while the long form scrolls
                    past it — the whole point is watching the change land. */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ position: { md: 'sticky' }, top: { md: '96px' } }}>
                    <Typography variant='body2' fontWeight='600' mb='8px'>Preview</Typography>
                    <GlobalPreview value={value} brandName={brandName} brandLogo={brandLogo} />
                    <Typography variant='caption' color='text.secondary' display='block' mt='10px'>
                      This frame wraps every page on your site.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </LoadingOverlay>
        )}
      </PagePaper>
    </Box>
  );
}
